/// Leaderboard cache mirroring mobile/lib/rank/handler/rank_handler.dart.

import { create } from 'zustand';

import * as userRepository from '@/user/data/userRepository';
import type { Ranking } from '@/user/domain/user';

export const rankPageSize = 25;

export interface RankBoard {
  entries: Ranking[];
  myRank: Ranking | null;
  hasMore: boolean;
  loadingMore: boolean;
  loading: boolean;
  error: string | null;
}

interface RankState extends RankBoard {
  loadInitial: (showLoading?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Failed to load leaderboard';
}

export const useRankStore = create<RankState>((set, get) => ({
  entries: [],
  myRank: null,
  hasMore: true,
  loadingMore: false,
  loading: true,
  error: null,

  loadInitial: async (showLoading = true) => {
    if (showLoading) set({ loading: true, error: null });
    try {
      const [entries, myRank] = await Promise.all([
        userRepository.getLeaderboard(rankPageSize, 0),
        userRepository.getMyRank(),
      ]);
      set({
        entries,
        myRank,
        hasMore: entries.length >= rankPageSize,
        loading: false,
        loadingMore: false,
        error: null,
      });
    } catch (error) {
      set({ loading: false, error: messageOf(error) });
    }
  },

  loadMore: async () => {
    const current = get();
    if (!current.hasMore || current.loadingMore) return;
    set({ loadingMore: true, error: null });
    try {
      const page = await userRepository.getLeaderboard(rankPageSize, current.entries.length);
      set({
        entries: [...current.entries, ...page],
        hasMore: page.length >= rankPageSize,
        loadingMore: false,
      });
    } catch (error) {
      set({ loadingMore: false, error: messageOf(error) });
    }
  },
}));
